// Map view — Google Maps AdvancedMarkerElement thumbnail markers, click→popup,
// card-hover→marker highlight, choices.js collection filter, mobile list toggle.
// No-op on pages without [data-map-view].

import { setOptions, importLibrary } from "@googlemaps/js-api-loader";

const MAPS_API_KEY = "AIzaSyAjgiTMjxGepkZYdJBkEdBH7Q0zbMMZW3k";
const MAP_ID = "b9dbd0c037bf537e";

export default function initMapView() {
	const root = document.querySelector("[data-map-view]");
	if (!root) return;

	const dataEl = root.querySelector("[data-map-data]");
	if (!dataEl) return;

	let data = [];
	try {
		data = JSON.parse(dataEl.textContent.trim());
	} catch (e) {
		console.error("Map data parse error:", e);
		return;
	}

	if (!data.length) return;

	const canvas = root.querySelector("[data-map-canvas]");
	const cardsEl = root.querySelector("[data-map-cards]");
	const cards = root.querySelectorAll(".map-view--card");
	const filterSelect = root.querySelector("[data-collection-filter]");
	const listToggle = root.querySelector("[data-list-toggle]");
	const listPanel = root.querySelector("[data-map-list]");

	setOptions({ key: MAPS_API_KEY });

	(async () => {
		const { Map } = await importLibrary("maps");
		const { AdvancedMarkerElement } = await importLibrary("marker");

		const map = new Map(canvas, {
			mapId: MAP_ID,
			disableDefaultUI: true,
			gestureHandling: "greedy",
		});

		// artworkId -> { marker, el, data }
		const markers = {};
		let infoWindow = null;
		let selectedId = null;

		// ----- Build markers -----
		const bounds = new google.maps.LatLngBounds();

		data.forEach(artwork => {
            console.log("Add marker for artwork:", artwork.id, artwork.title);
			const el = document.createElement("button");
			el.type = "button";
			el.className = "map-marker";
			if (artwork.thumbnail) {
				el.innerHTML =
					'<img class="map-marker--img" src="' +
					artwork.thumbnail.url +
					'" alt="" width="30" height="30">';
			}

			const marker = new AdvancedMarkerElement({
				map,
				position: { lat: artwork.lat, lng: artwork.lng },
				content: el,
				title: artwork.title,
			});

			markers[artwork.id] = { marker, el, data: artwork };
			bounds.extend({ lat: artwork.lat, lng: artwork.lng });

			marker.addListener("click", () => {
				openPopup(artwork.id);
			});
		});

		map.fitBounds(bounds);

		// ----- Popup (InfoWindow) -----
		function openPopup(id) {
			const entry = markers[id];
			if (!entry) return;

			// Clear previous selection
			if (selectedId !== null && markers[selectedId]) {
				markers[selectedId].el.classList.remove("is-selected");
			}

			selectedId = id;
			entry.el.classList.add("is-selected");

			const a = entry.data;
			const tags = (a.collections || [])
				.map(
					c =>
						'<span class="map-popup--tag">' +
						escapeHtml(c.title) +
						"</span>"
				)
				.join("");

			const container = document.createElement("a");
			container.className = "map-popup";
			if (a.url) {
				container.href = a.url;
			}
			container.innerHTML =
				'<button class="map-popup--close" data-popup-close aria-label="Close">×</button>' +
				'<div class="map-popup--image">' +
				(a.preview
					? '<img src="' +
					  a.preview.url +
					  '" alt="' +
					  escapeHtml(a.title) +
					  '">'
					: "") +
				tags +
				"</div>" +
				'<div class="map-popup--body">' +
				'<p class="map-popup--label">Created by:</p>' +
				'<p class="map-popup--artist">' +
				escapeHtml((a.artists || []).join(", ")) +
				"</p>" +
				"</div>";

			container
				.querySelector("[data-popup-close]")
				.addEventListener("click", e => {
					e.preventDefault();
					e.stopPropagation();
					closePopup();
				});

			if (!infoWindow) {
				infoWindow = new google.maps.InfoWindow({
					disableAutoPan: false,
				});
			}
			infoWindow.setContent(container);
			infoWindow.open(map, entry.marker);
		}

		function closePopup() {
			if (infoWindow) {
				infoWindow.close();
			}
			if (selectedId !== null && markers[selectedId]) {
				markers[selectedId].el.classList.remove("is-selected");
			}
			selectedId = null;
		}

		// ----- Card ↔ marker interaction -----
		cards.forEach(card => {
			const id = parseInt(card.getAttribute("data-artwork-id"), 10);
			const entry = markers[id];
			if (!entry) return;

			card.addEventListener("mouseenter", () => {
				entry.el.classList.add("is-hover");
				entry.marker.zIndex = 999;
			});
			card.addEventListener("mouseleave", () => {
				entry.el.classList.remove("is-hover");
				entry.marker.zIndex = null;
			});
			card.addEventListener("focusin", () => {
				entry.el.classList.add("is-hover");
			});
			card.addEventListener("focusout", () => {
				entry.el.classList.remove("is-hover");
			});
		});

		// ----- Collection filter -----
		if (filterSelect) {
			const onChange = () => {
				const slug = filterSelect.value || "";
				const visibleBounds = new google.maps.LatLngBounds();
				let visibleCount = 0;

				cards.forEach(card => {
					const collections = (
						card.getAttribute("data-collections") || ""
					).trim().split(/\s+/);
					const show = !slug || collections.indexOf(slug) !== -1;
					card.classList.toggle("is-hidden", !show);
				});

				data.forEach(artwork => {
					const entry = markers[artwork.id];
					if (!entry) return;
					const collectionSlugs = (artwork.collections || []).map(
						c => c.slug
					);
					const matches = !slug || collectionSlugs.indexOf(slug) !== -1;
					entry.marker.map = matches ? map : null;
					if (matches) {
						visibleBounds.extend({
							lat: artwork.lat,
							lng: artwork.lng,
						});
						visibleCount++;
					}
				});

				if (visibleCount > 0) {
					map.fitBounds(visibleBounds);
				}

				// Close popup if the selected artwork is now hidden
				if (
					selectedId !== null &&
					markers[selectedId] &&
					markers[selectedId].marker.map === null
				) {
					closePopup();
				}
			};

			filterSelect.addEventListener("change", onChange);
		}

		// ----- Mobile list toggle -----
		if (listToggle && listPanel) {
			listToggle.addEventListener("click", () => {
				const isOpen = listPanel.classList.toggle("is-open");
				listToggle.textContent = isOpen ? "Hide list" : "Show list";
				listToggle.setAttribute("aria-expanded", isOpen ? "true" : "false");
			});
		}
	})();
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str == null ? "" : String(str);
	return div.innerHTML;
}

// Artwork detail page — renders a single marker for the artwork's location
// inside the banner area (same aspect ratio as the featured image banner).
// No-op on pages without [data-artwork-map-view].
export function initArtworkDetailMap() {
	const root = document.querySelector("[data-artwork-map-view]");
	if (!root) return;

	const dataEl = root.querySelector("[data-artwork-map-data]");
	if (!dataEl) return;

	let data = [];
	try {
		data = JSON.parse(dataEl.textContent.trim());
	} catch (e) {
		console.error("Artwork map data parse error:", e);
		return;
	}

	if (!data.length) return;

	const canvas = root.querySelector("[data-artwork-map-canvas]");
	if (!canvas) return;

	setOptions({ key: MAPS_API_KEY });

	(async () => {
		const { Map } = await importLibrary("maps");
		const { AdvancedMarkerElement } = await importLibrary("marker");

		const map = new Map(canvas, {
			mapId: MAP_ID,
			disableDefaultUI: true,
			gestureHandling: "none",
			scrollwheel: false,
			disableDoubleClickZoom: true,
		});

		const bounds = new google.maps.LatLngBounds();

		data.forEach(artwork => {
			const el = document.createElement("button");
			el.type = "button";
			el.className = "map-marker";
			if (artwork.thumbnail) {
				el.innerHTML =
					'<img class="map-marker--img" src="' +
					artwork.thumbnail.url +
					'" alt="" width="30" height="30">';
			}

			const marker = new AdvancedMarkerElement({
				map,
				position: { lat: artwork.lat, lng: artwork.lng },
				content: el,
				title: artwork.title,
			});

			bounds.extend({ lat: artwork.lat, lng: artwork.lng });

			marker.addListener("click", () => {
				if (artwork.url) {
					window.location.href = artwork.url;
				}
			});
		});

		map.fitBounds(bounds);

		// fitBounds can zoom too far for a single point; cap the zoom level
		google.maps.event.addListenerOnce(map, "idle", () => {
			if (map.getZoom() > 16) map.setZoom(16);
		});
	})();
}
